import { useCallback, useEffect, useState } from 'react'
import {
  Stack,
  Tabs,
  Text,
  Title,
  Button,
  Group,
  Badge,
  Card,
  Modal,
  TextInput,
  Textarea,
  ActionIcon,
  Paper,
  Alert,
  ScrollArea,
  Divider,
  Box,
} from '@mantine/core'
import { Salvo, SalvoCrosspoint } from '@mediaconnect-router/shared'
import { SalvosService } from '../services/SalvosService'
import { useRouting, routingActions } from '../contexts/RoutingContext'

export interface SalvosPanelProps {
  service: SalvosService
  /** Whether the current user is in the Cognito admins group. */
  isAdmin?: boolean
  /** Callback used for optimistic UI when firing — provided by RouterInterface. */
  onFireApplied?: (crosspoints: SalvoCrosspoint[]) => void
  onError?: (message: string) => void
}

/**
 * Right-side panel with three tabs:
 *   "My Salvos"       — caller's private library + pending submissions
 *   "Team Salvos"     — published salvos fireable by anyone
 *   "Pending Review"  — admin-only queue (hidden for non-admins)
 *
 * A "Save as Salvo" button appears whenever the user has a non-empty
 * shift-click selection in the matrix.
 */
export function SalvosPanel({ service, isAdmin = false, onFireApplied, onError }: SalvosPanelProps) {
  const { state, dispatch } = useRouting()
  const [myList, setMyList] = useState<Salvo[]>([])
  const [sharedList, setSharedList] = useState<Salvo[]>([])
  const [pendingList, setPendingList] = useState<Salvo[]>([])
  const [loadingTab, setLoadingTab] = useState<string | null>('my')
  const [saveOpen, setSaveOpen] = useState(false)
  const [savedName, setSavedName] = useState('')
  const [savedDescription, setSavedDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<string | null>('my')

  const refreshMy = useCallback(async () => {
    try {
      setLoadingTab('my')
      setMyList(await service.list())
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to load salvos')
    } finally {
      setLoadingTab(null)
    }
  }, [service, onError])

  const refreshShared = useCallback(async () => {
    try {
      setLoadingTab('shared')
      setSharedList(await service.listShared())
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to load shared salvos')
    } finally {
      setLoadingTab(null)
    }
  }, [service, onError])

  const refreshPending = useCallback(async () => {
    try {
      setLoadingTab('pending')
      setPendingList(await service.listPending())
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to load pending salvos')
    } finally {
      setLoadingTab(null)
    }
  }, [service, onError])

  useEffect(() => {
    refreshMy()
  }, [refreshMy])

  // Refresh the tab the user switches into.
  useEffect(() => {
    if (activeTab === 'shared') refreshShared()
    else if (activeTab === 'pending') refreshPending()
    else if (activeTab === 'my') refreshMy()
  }, [activeTab, refreshMy, refreshPending, refreshShared])

  async function handleFire(salvo: Salvo) {
    try {
      const result = await service.fire(salvo.salvoId)
      onFireApplied?.(salvo.crosspoints)
      // Refresh current tab data if visible
      if (activeTab === 'my') refreshMy()
      return result
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to fire salvo')
    }
    return undefined
  }

  async function handlePublish(salvo: Salvo) {
    try {
      await service.publish(salvo.salvoId)
      refreshMy()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to publish salvo')
    }
  }

  async function handleDelete(salvo: Salvo) {
    try {
      await service.remove(salvo.salvoId)
      refreshMy()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to delete salvo')
    }
  }

  async function handleApprove(salvo: Salvo) {
    try {
      await service.approve(salvo.ownerId, salvo.salvoId)
      refreshPending()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to approve salvo')
    }
  }

  async function handleReject(salvo: Salvo) {
    try {
      await service.reject(salvo.ownerId, salvo.salvoId)
      refreshPending()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to reject salvo')
    }
  }

  async function handleSaveSubmit() {
    if (!savedName.trim() || state.ui.salvoSelection.length === 0) return
    setSaving(true)
    try {
      const trimmedDesc = savedDescription.trim()
      await service.create({
        name: savedName.trim(),
        ...(trimmedDesc ? { description: trimmedDesc } : {}),
        crosspoints: state.ui.salvoSelection,
      })
      setSaveOpen(false)
      setSavedName('')
      setSavedDescription('')
      dispatch(routingActions.clearSalvoSelection())
      refreshMy()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to save salvo')
    } finally {
      setSaving(false)
    }
  }

  const selection = state.ui.salvoSelection

  return (
    <Paper p="md" withBorder radius="md" style={{ width: 360 }}>
      <Stack gap="sm">
        <Title order={4}>Salvos</Title>

        {selection.length > 0 && (
          <Alert color="blue" variant="light" data-testid="salvo-selection-alert">
            <Group justify="space-between" align="center">
              <Text size="sm">
                <b>{selection.length}</b> crosspoint{selection.length === 1 ? '' : 's'} selected
              </Text>
              <Group gap="xs">
                <Button size="xs" onClick={() => setSaveOpen(true)} data-testid="save-as-salvo-btn">
                  Save as Salvo
                </Button>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={() => dispatch(routingActions.clearSalvoSelection())}
                  aria-label="Clear selection"
                >
                  ×
                </ActionIcon>
              </Group>
            </Group>
          </Alert>
        )}

        <Text size="xs" c="dimmed">
          Tip: Hold Shift and click crosspoints to build a salvo.
        </Text>

        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="my">My Salvos</Tabs.Tab>
            <Tabs.Tab value="shared">Team Salvos</Tabs.Tab>
            {isAdmin && <Tabs.Tab value="pending" data-testid="admin-pending-tab">Pending Review</Tabs.Tab>}
          </Tabs.List>

          <Tabs.Panel value="my" pt="sm">
            <ScrollArea.Autosize mah={480}>
              {loadingTab === 'my' ? (
                <Text size="sm" c="dimmed">Loading…</Text>
              ) : myList.length === 0 ? (
                <Text size="sm" c="dimmed">No private salvos yet. Shift-click in the matrix to build one.</Text>
              ) : (
                <Stack gap="xs">
                  {myList.map(s => (
                    <Card key={s.salvoId} withBorder padding="sm">
                      <Stack gap={4}>
                        <Group justify="space-between">
                          <Text fw={500}>{s.name}</Text>
                          <Badge size="xs" variant="light" color={visibilityColor(s.visibility)}>
                            {s.visibility}
                          </Badge>
                        </Group>
                        {s.description && <Text size="xs" c="dimmed">{s.description}</Text>}
                        <Text size="xs" c="dimmed">{s.crosspoints.length} crosspoint{s.crosspoints.length === 1 ? '' : 's'}</Text>
                        <Group gap="xs" mt="xs">
                          <Button size="compact-xs" onClick={() => handleFire(s)} data-testid={`fire-${s.salvoId}`}>Fire</Button>
                          {s.visibility === 'private' && (
                            <Button size="compact-xs" variant="light" onClick={() => handlePublish(s)}>Publish</Button>
                          )}
                          <Button size="compact-xs" variant="subtle" color="red" onClick={() => handleDelete(s)}>Delete</Button>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </ScrollArea.Autosize>
          </Tabs.Panel>

          <Tabs.Panel value="shared" pt="sm">
            <ScrollArea.Autosize mah={480}>
              {loadingTab === 'shared' ? (
                <Text size="sm" c="dimmed">Loading…</Text>
              ) : sharedList.length === 0 ? (
                <Text size="sm" c="dimmed">No team salvos yet.</Text>
              ) : (
                <Stack gap="xs">
                  {sharedList.map(s => (
                    <Card key={s.salvoId} withBorder padding="sm">
                      <Stack gap={4}>
                        <Text fw={500}>{s.name}</Text>
                        {s.description && <Text size="xs" c="dimmed">{s.description}</Text>}
                        <Text size="xs" c="dimmed">{s.crosspoints.length} crosspoints · by {s.publishedBy ?? '?'}</Text>
                        <Button size="compact-xs" onClick={() => handleFire(s)}>Fire</Button>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </ScrollArea.Autosize>
          </Tabs.Panel>

          {isAdmin && (
            <Tabs.Panel value="pending" pt="sm">
              <ScrollArea.Autosize mah={480}>
                {loadingTab === 'pending' ? (
                  <Text size="sm" c="dimmed">Loading…</Text>
                ) : pendingList.length === 0 ? (
                  <Text size="sm" c="dimmed">Queue is empty.</Text>
                ) : (
                  <Stack gap="xs">
                    {pendingList.map(s => (
                      <Card key={s.salvoId} withBorder padding="sm">
                        <Stack gap={4}>
                          <Group justify="space-between">
                            <Text fw={500}>{s.name}</Text>
                            <Text size="xs" c="dimmed">by {s.ownerId}</Text>
                          </Group>
                          {s.description && <Text size="xs" c="dimmed">{s.description}</Text>}
                          <Text size="xs" c="dimmed">{s.crosspoints.length} crosspoints</Text>
                          <Group gap="xs" mt="xs">
                            <Button size="compact-xs" color="green" onClick={() => handleApprove(s)} data-testid={`approve-${s.salvoId}`}>Approve</Button>
                            <Button size="compact-xs" color="red" variant="light" onClick={() => handleReject(s)}>Reject</Button>
                          </Group>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </ScrollArea.Autosize>
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>

      <Modal opened={saveOpen} onClose={() => setSaveOpen(false)} title="Save as Salvo" data-testid="save-salvo-modal">
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="Commercial Break"
            value={savedName}
            onChange={e => setSavedName(e.currentTarget.value)}
            required
            maxLength={100}
            data-testid="salvo-name-input"
          />
          <Textarea
            label="Description"
            placeholder="Optional details for your team"
            value={savedDescription}
            onChange={e => setSavedDescription(e.currentTarget.value)}
            maxLength={500}
            minRows={2}
          />
          <Divider />
          <Text size="xs" c="dimmed">Crosspoints ({selection.length})</Text>
          <Box style={{ maxHeight: 180, overflowY: 'auto' }}>
            <Stack gap={2}>
              {selection.map(cp => (
                <Text key={`${cp.sourceId}-${cp.destinationId}`} size="xs" ff="monospace">
                  {cp.sourceId} → {cp.destinationId}
                </Text>
              ))}
            </Stack>
          </Box>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveSubmit}
              loading={saving}
              disabled={!savedName.trim() || selection.length === 0}
              data-testid="salvo-save-confirm"
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}

function visibilityColor(v: Salvo['visibility']) {
  switch (v) {
    case 'published': return 'green'
    case 'pending': return 'yellow'
    default: return 'gray'
  }
}
